import React, { useCallback, useEffect, useRef, useState } from "react";
import FinderWindow from "./FinderWindow";
import NotesWindow from "./NotesWindow";
import ChromeWindow from "./ChromeWindow";
import GhosttyWindow from "./GhosttyWindow";
import OrbStackWindow from "./OrbStack/OrbStackWindow";
import SpotifyWindow from "./Spotify/SpotifyWindow";
import ZedWindow from "./Zed/ZedWindow";
import {
  showChrome,
  showGhostty,
  showNotes,
  showOrbStack,
  showSpotify,
  showWindow,
  showZed,
} from "../redux/windowSlice";
import { setCurrentApp, setCurrentSettings } from "../redux/appSlice";
import { useDispatch, useSelector } from "react-redux";
import MenuBar from "./MenuBar";
import notes from "../assets/icons/macos/notes.png";

const MENUS = {
  Finder: ["File", "Edit", "View", "Go", "Window", "Help"],
  Notes: ["File", "Edit", "Format", "View", "Window", "Help"],
  Chrome: ["File", "Edit", "View", "History", "Bookmarks", "Profiles", "Tab", "Window", "Help"],
  Ghostty: ["File", "Edit", "View", "Window", "Help"],
  OrbStack: ["File", "Edit", "View", "Window", "Help"],
  Spotify: ["File", "Edit", "View", "Playback", "Window", "Help"],
  Zed: ["File", "Edit", "Selection", "View", "Go", "Run", "Window", "Help"],
};

const Desktop = () => {
  const { snotes, show: finderOpen, schrome, sghostty, sorbstack, sspotify, szed } = useSelector(
    (state) => state.window
  );
  const { currentApp } = useSelector((state) => state.app);
  const disptach = useDispatch();
  const [iconSelected, setIconSelected] = useState(false);
  // Which window is in front; the menu bar follows it.
  const [front, setFront] = useState("Notes");
  // The area windows live in (between the menu bar and the Dock), tracked as the browser resizes.
  const workspaceRef = useRef(null);
  const [workspace, setWorkspace] = useState(() => ({ w: window.innerWidth, h: window.innerHeight - 28 - 84 }));

  useEffect(() => {
    const el = workspaceRef.current;
    if (!el) return undefined;
    const measure = () => setWorkspace({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const focus = useCallback(
    (app) => {
      setFront(app);
      disptach(setCurrentApp(app));
      disptach(setCurrentSettings(MENUS[app]));
    },
    [disptach]
  );

  // Every visit starts with the resume open in Notes, in front. (Whether Notes was open is saved
  // between visits, so this sets it rather than toggling it.)
  useEffect(() => {
    disptach(showNotes(true));
    focus("Notes");
  }, [disptach, focus]);

  // A window that opens comes to the front, however it was opened (Dock, Spotlight, desktop icon).
  const was = useRef({ snotes, finderOpen, schrome, sghostty, sorbstack, sspotify, szed });
  useEffect(() => {
    if (snotes && !was.current.snotes) setFront("Notes");
    if (finderOpen && !was.current.finderOpen) setFront("Finder");
    if (schrome && !was.current.schrome) setFront("Chrome");
    if (sghostty && !was.current.sghostty) setFront("Ghostty");
    if (sorbstack && !was.current.sorbstack) setFront("OrbStack");
    if (sspotify && !was.current.sspotify) setFront("Spotify");
    if (szed && !was.current.szed) setFront("Zed");
    was.current = { snotes, finderOpen, schrome, sghostty, sorbstack, sspotify, szed };
  }, [snotes, finderOpen, schrome, sghostty, sorbstack, sspotify, szed]);

  // The Dock's app icons open their window, or bring it forward if it's open.
  useEffect(() => {
    const onOpenFinder = () => {
      disptach(showWindow(true));
      focus("Finder");
    };
    const onOpenChrome = () => {
      disptach(showChrome(true));
      focus("Chrome");
    };
    const onOpenGhostty = () => {
      disptach(showGhostty(true));
      focus("Ghostty");
    };
    const onOpenOrbStack = () => {
      disptach(showOrbStack(true));
      focus("OrbStack");
    };
    const onOpenSpotify = () => {
      disptach(showSpotify(true));
      focus("Spotify");
    };
    const onOpenZed = () => {
      disptach(showZed(true));
      focus("Zed");
    };
    const onOpenResume = () => setFront("Notes");
    window.addEventListener("open-finder", onOpenFinder);
    window.addEventListener("open-chrome", onOpenChrome);
    window.addEventListener("open-ghostty", onOpenGhostty);
    window.addEventListener("open-orbstack", onOpenOrbStack);
    window.addEventListener("open-spotify", onOpenSpotify);
    window.addEventListener("open-zed", onOpenZed);
    window.addEventListener("open-resume", onOpenResume);
    return () => {
      window.removeEventListener("open-finder", onOpenFinder);
      window.removeEventListener("open-chrome", onOpenChrome);
      window.removeEventListener("open-ghostty", onOpenGhostty);
      window.removeEventListener("open-orbstack", onOpenOrbStack);
      window.removeEventListener("open-spotify", onOpenSpotify);
      window.removeEventListener("open-zed", onOpenZed);
      window.removeEventListener("open-resume", onOpenResume);
    };
  }, [disptach, focus]);

  const openResume = () => {
    setIconSelected(false);
    if (!snotes) disptach(showNotes(true));
    focus("Notes");
  };

  const closeFinder = () => {
    disptach(showWindow(false));
    focus(snotes ? "Notes" : schrome ? "Chrome" : sghostty ? "Ghostty" : "Finder");
  };

  const closeChrome = () => {
    disptach(showChrome(false));
    focus(snotes || !(finderOpen || sghostty) ? "Notes" : sghostty ? "Ghostty" : "Finder");
  };

  const closeGhostty = () => {
    disptach(showGhostty(false));
    focus(snotes ? "Notes" : schrome ? "Chrome" : sorbstack ? "OrbStack" : "Finder");
  };

  const closeOrbStack = () => {
    disptach(showOrbStack(false));
    focus(snotes ? "Notes" : schrome ? "Chrome" : sghostty ? "Ghostty" : "Finder");
  };

  const closeZed = () => {
    disptach(showZed(false));
    focus(snotes ? "Notes" : schrome ? "Chrome" : "Finder");
  };

  const closeSpotify = () => {
    disptach(showSpotify(false));
    focus(snotes ? "Notes" : schrome ? "Chrome" : sorbstack ? "OrbStack" : "Finder");
  };

  return (
    <div className="no-select fixed inset-0" onMouseDown={() => setIconSelected(false)}>
      <MenuBar />
      <button
        type="button"
        onMouseDown={(e) => {
          e.stopPropagation();
          setIconSelected(true);
        }}
        onDoubleClick={openResume}
        onKeyDown={(e) => e.key === "Enter" && openResume()}
        className={`desktop-icon ${iconSelected ? "is-selected" : ""}`}
        aria-label="Resume. Double-click to open in Notes"
      >
        <span className="desktop-icon-img">
          <img src={notes} alt="" draggable={false} className="w-[64px] h-[64px]" />
        </span>
        <span className="desktop-icon-label">Resume</span>
      </button>
      <div className="workspace" ref={workspaceRef}>
        <FinderWindow
          workspace={workspace}
          open={finderOpen}
          front={front === "Finder"}
          onFocus={() => (front !== "Finder" || currentApp !== "Finder") && focus("Finder")}
          onClose={closeFinder}
        />
        <NotesWindow workspace={workspace} front={front === "Notes"} onFocus={() => (front !== "Notes" || currentApp !== "Notes") && focus("Notes")} />
        <ChromeWindow
          workspace={workspace}
          open={schrome}
          front={front === "Chrome"}
          onFocus={() => (front !== "Chrome" || currentApp !== "Chrome") && focus("Chrome")}
          onClose={closeChrome}
        />
        <GhosttyWindow
          workspace={workspace}
          open={sghostty}
          front={front === "Ghostty"}
          onFocus={() => (front !== "Ghostty" || currentApp !== "Ghostty") && focus("Ghostty")}
          onClose={closeGhostty}
        />
        <OrbStackWindow
          workspace={workspace}
          open={sorbstack}
          front={front === "OrbStack"}
          onFocus={() => (front !== "OrbStack" || currentApp !== "OrbStack") && focus("OrbStack")}
          onClose={closeOrbStack}
        />
        <SpotifyWindow
          workspace={workspace}
          open={sspotify}
          front={front === "Spotify"}
          onFocus={() => (front !== "Spotify" || currentApp !== "Spotify") && focus("Spotify")}
          onClose={closeSpotify}
        />
        <ZedWindow
          workspace={workspace}
          open={szed}
          front={front === "Zed"}
          onFocus={() => (front !== "Zed" || currentApp !== "Zed") && focus("Zed")}
          onClose={closeZed}
        />
      </div>
    </div>
  );
};

export default Desktop;
